import { useEffect, useState } from 'react';
import { FileText, LockFill, PeopleFill, ShieldFill, X } from 'react-bootstrap-icons';
import { LANDING_LEGAL_URLS } from '../../utils/landingLegalUrls';
import './RecruiterAccessPolicyModal.css';

type PolicyLink = {
  label: string;
  href: string;
  icon: 'terms' | 'community' | 'content' | 'privacy';
};

type RecruiterAccessPolicyModalProps = {
  show: boolean;
  onClose: () => void;
  onContinue: () => void;
  policyLinks?: PolicyLink[];
};

const DEFAULT_POLICY_LINKS: PolicyLink[] = [
  { label: 'Terms of Service', href: LANDING_LEGAL_URLS.termsOfService, icon: 'terms' },
  { label: 'Community Policy', href: LANDING_LEGAL_URLS.communityStandards, icon: 'community' },
  {
    label: 'Candidate Content Protection Policy',
    href: LANDING_LEGAL_URLS.candidateContentPolicy,
    icon: 'content',
  },
  { label: 'Privacy Policy', href: LANDING_LEGAL_URLS.privacyPolicy, icon: 'privacy' },
];

function PolicyIcon({ type }: { type: PolicyLink['icon'] }) {
  if (type === 'community') return <PeopleFill size={18} aria-hidden />;
  if (type === 'content') return <ShieldFill size={18} aria-hidden />;
  if (type === 'privacy') return <LockFill size={18} aria-hidden />;
  return <FileText size={18} aria-hidden />;
}

export function RecruiterAccessPolicyModal({
  show,
  onClose,
  onContinue,
  policyLinks = DEFAULT_POLICY_LINKS,
}: RecruiterAccessPolicyModalProps) {
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!show) {
      setAccepted(false);
    }
  }, [show]);

  if (!show) return null;

  return (
    <div className="recruiter-policy-overlay" role="presentation">
      <div
        className="recruiter-policy-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recruiter-policy-title"
      >
        <button
          type="button"
          className="recruiter-policy-close"
          aria-label="Close"
          onClick={onClose}
        >
          <X size={20} aria-hidden />
        </button>

        <div className="recruiter-policy-hero">
          <div className="recruiter-policy-hero__icon" aria-hidden>
            <ShieldFill size={24} />
          </div>
          <h2 id="recruiter-policy-title">Recruiter Access</h2>
          <p>
            To protect our candidates and their content, please confirm that you agree to the
            following policies.
          </p>
        </div>

        <div className="recruiter-policy-links">
          {policyLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="recruiter-policy-link"
            >
              <span className="recruiter-policy-link__icon">
                <PolicyIcon type={link.icon} />
              </span>
              <span>{link.label}</span>
            </a>
          ))}
        </div>

        <label className="recruiter-policy-check">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)}
          />
          <span>
            I confirm that I have read, understood, and agree to follow the SkipScreen{' '}
            {policyLinks.map((link, index) => (
              <span key={link.href}>
                <a href={link.href} target="_blank" rel="noopener noreferrer">
                  {link.label}
                </a>
                {index < policyLinks.length - 1 ? ', ' : '.'}
              </span>
            ))}
          </span>
        </label>

        <div className="recruiter-policy-note">
          <LockFill size={16} aria-hidden />
          <span>You must agree to these policies to view candidate content.</span>
        </div>

        <button
          type="button"
          className="recruiter-policy-continue"
          disabled={!accepted}
          onClick={onContinue}
        >
          <LockFill size={16} aria-hidden />
          Continue to Candidate Profile
        </button>
      </div>
    </div>
  );
}

export default RecruiterAccessPolicyModal;
